import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Check, Calendar, Phone, ArrowRight, Clock, Shield } from "lucide-react";
import { Button } from "../components/ui/button";
import { SiteFooter } from "../components/SiteFooter";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_da98dbcd-f751-4482-bd4e-a7e20426ba71/artifacts/tm6wbpvj_CD8D187A-3FEA-430F-A897-685C7F8A2178.png";

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const [confirmation, setConfirmation] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchConfirmation = useCallback(async () => {
    try {
      const sessionId = searchParams.get("session_id");
      const subId = searchParams.get("submission_id");
      const params = {};
      if (sessionId) params.session_id = sessionId;
      if (subId) params.submission_id = subId;

      const res = await axios.get(`${API}/payment-confirmation`, { params });
      setConfirmation(res.data);
    } catch {
      setConfirmation({
        found: false,
        type: "unknown",
        headline: "Payment confirmed",
        message: "Thank you for your payment. We'll be in touch shortly to schedule your session.",
        calendly_url: null,
        button_text: null,
      });
    } finally {
      setLoading(false);
    }
  }, [searchParams]);

  useEffect(() => {
    // Small delay to let webhook process first
    const timer = setTimeout(fetchConfirmation, 1500);
    return () => clearTimeout(timer);
  }, [fetchConfirmation]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--bg-primary)" }}>
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-2 border-t-transparent rounded-full mx-auto mb-4" style={{ borderColor: "var(--accent)", borderTopColor: "transparent" }} />
          <p className="text-sm" style={{ color: "var(--text-tertiary)" }}>Confirming your payment...</p>
        </div>
      </div>
    );
  }

  const c = confirmation;
  const isPending = c?.type === "private_access_pending";

  return (
    <div className="min-h-screen flex items-center justify-center px-6" style={{ background: "var(--bg-primary)" }} data-testid="payment-success-page">
      <div className="w-full max-w-md text-center">
        <img src={LOGO_URL} alt="Green Routes" className="h-10 mx-auto mb-10 logo-blend" />

        {/* Status Icon */}
        <div className="inline-flex items-center justify-center w-16 h-16 mb-6" style={{
          background: isPending ? "rgba(217,119,6,0.1)" : "rgba(34,197,94,0.1)",
          border: `1px solid ${isPending ? "rgba(217,119,6,0.2)" : "rgba(34,197,94,0.2)"}`,
        }}>
          {isPending
            ? <Clock size={32} style={{ color: "#D97706" }} />
            : <Check size={32} style={{ color: "var(--success)" }} />
          }
        </div>

        {/* Headline */}
        <h1 className="text-2xl sm:text-3xl tracking-tight mb-3" style={{ color: "var(--text-primary)" }} data-testid="success-title">
          {c?.headline || "Payment confirmed"}
        </h1>
        <p className="text-sm mb-6" style={{ color: "var(--text-secondary)", lineHeight: 1.7 }}>
          {c?.message}
        </p>

        {/* Calendly Booking Card — only shown when URL is available */}
        {c?.calendly_url && (
          <div className="mt-6 p-6" style={{ background: "var(--bg-secondary)", border: "1px solid var(--border)" }} data-testid="calendly-booking-card">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 flex items-center justify-center" style={{ background: "rgba(15,50,33,0.06)", border: "1px solid rgba(15,50,33,0.12)" }}>
                <Calendar size={18} style={{ color: "var(--accent)" }} />
              </div>
              <div className="text-left">
                <p className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>Book Your Session</p>
                <p className="text-xs" style={{ color: "var(--text-tertiary)" }}>Select a time that works for you</p>
              </div>
            </div>

            <a
              href={c.calendly_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="calendly-booking-btn"
              className="flex items-center justify-center gap-2 w-full py-3.5 text-sm font-semibold transition-all"
              style={{ background: "var(--accent)", color: "#FFFFFF" }}
            >
              <Calendar size={14} />
              {c.button_text}
            </a>
          </div>
        )}

        {/* Pending Approval Card — shown when admin needs to approve */}
        {isPending && (
          <div className="mt-6 p-6" style={{ background: "rgba(217,119,6,0.04)", border: "1px solid rgba(217,119,6,0.15)" }} data-testid="pending-approval-card">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center" style={{ background: "rgba(217,119,6,0.08)", border: "1px solid rgba(217,119,6,0.2)" }}>
                <Shield size={18} style={{ color: "#D97706" }} />
              </div>
              <div className="text-left">
                <p className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>Under Review</p>
                <p className="text-xs" style={{ color: "var(--text-tertiary)" }}>Private access requires approval</p>
              </div>
            </div>
            <div
              className="flex items-center justify-center gap-2 w-full py-3.5 text-sm font-semibold"
              style={{ background: "rgba(15,50,33,0.04)", color: "var(--text-tertiary)", border: "1px solid var(--border)", cursor: "default" }}
              data-testid="pending-approval-btn"
            >
              <Clock size={14} />
              {c.button_text}
            </div>
          </div>
        )}

        {/* Fallback — no Calendly URL and not pending (unknown payment) */}
        {!c?.calendly_url && !isPending && (
          <div className="mt-6 p-6" style={{ background: "var(--bg-secondary)", border: "1px solid var(--border)" }}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center" style={{ background: "rgba(15,50,33,0.06)", border: "1px solid rgba(15,50,33,0.12)" }}>
                <Calendar size={18} style={{ color: "var(--accent)" }} />
              </div>
              <div className="text-left">
                <p className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>Schedule Your Session</p>
                <p className="text-xs" style={{ color: "var(--text-tertiary)" }}>Our team will reach out within 24 hours</p>
              </div>
            </div>
            <a
              href="https://buy.stripe.com/test_3cI3cw76afo3dj8ekM2cg00"
              target="_blank" rel="noopener noreferrer"
              data-testid="btn-call-now"
              className="flex items-center justify-center gap-2 w-full py-3 text-sm font-semibold transition-colors"
              style={{ background: "var(--accent)", color: "#FFFFFF" }}
            >
              <Phone size={14} /> Book a Consultation — $50
            </a>
          </div>
        )}

        {/* Track status link */}
        <Link to="/track" data-testid="link-track-status">
          <Button variant="ghost" className="mt-5 text-sm" style={{ color: "var(--text-secondary)", border: "1px solid var(--border)" }}>
            Track Your Status <ArrowRight size={14} className="ml-2" />
          </Button>
        </Link>

        <div className="mt-8">
          <SiteFooter />
        </div>
      </div>
    </div>
  );
}
